import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, ArrowUpCircle, Smartphone, Settings2, CheckCircle2, Sparkles } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { getInstallContext } from "@/lib/device";
import { toast } from "sonner";

const DISMISS_KEY = "rc_apk_dismissed";     // version the user dismissed the banner for
const INSTALLED_KEY = "rc_apk_installed";   // version the user chose to update to (optimistic)
const BASELINE_KEY = "rc_apk_current";      // version considered already installed (baseline)

const fmtBytes = (b) => {
  if (!b || b <= 0) return "";
  const mb = b / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};

// Trigger the APK download. Native (Median WebView) → try the bridge, then fall back to an
// anchor click which the WebView's DownloadManager picks up and prompts to install. PWA/Chrome
// → plain anchor download (backend sends Content-Disposition: attachment).
function triggerDownload(url, version, native) {
  if (native) {
    try {
      const b = window.median || window.gonative;
      if (b?.share?.downloadFile) { b.share.downloadFile({ url }); return; }
      if (b?.window?.open) { b.window.open({ url }); return; }
    } catch (e) { /* fall through */ }
  }
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", `RoyalChat-${version || "latest"}.apk`);
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function AndroidUpdateBanner() {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const ctx = useMemo(() => getInstallContext(), []);
  const [latest, setLatest] = useState(null);   // { available, version, size, url }
  const [modalOpen, setModalOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  // The version we consider already installed. On the very first run we baseline
  // this to the current latest so a freshly-installed user is NOT prompted to update.
  const [installedVersion, setInstalledVersion] = useState(
    typeof window !== "undefined" ? localStorage.getItem(BASELINE_KEY) : null
  );

  useEffect(() => {
    // iOS / desktop must NEVER fetch or prompt.
    if (!ctx.isAndroid) return;
    api.get("/app/android/latest").then(({ data }) => {
      setLatest(data);
      if (data?.available && data.version) {
        let base = localStorage.getItem(BASELINE_KEY);
        if (!base) {
          // First launch after install → treat the current latest as installed.
          base = data.version;
          try { localStorage.setItem(BASELINE_KEY, base); } catch (e) {}
        }
        setInstalledVersion(base);
      }
    }).catch(() => {});
  }, [ctx.isAndroid]);

  const version = latest?.version || "";
  const apkUrl = `${BACKEND_URL}/api/app/android/apk`;

  const seenDismissed = typeof window !== "undefined" ? localStorage.getItem(DISMISS_KEY) : null;
  const seenInstalled = typeof window !== "undefined" ? localStorage.getItem(INSTALLED_KEY) : null;

  // Only prompt when the published version differs from the one we baselined as installed.
  const show = ctx.isAndroid && latest?.available && version && installedVersion
    && version !== installedVersion
    && version !== seenDismissed && version !== seenInstalled && !dismissed;

  if (!ctx.isAndroid || !latest?.available) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, version); } catch (e) {}
    setDismissed(true);
    setModalOpen(false);
  };

  const doUpdate = () => {
    triggerDownload(apkUrl, version, ctx.isNativeAndroid);
    if (ctx.isNativeAndroid) {
      try { localStorage.setItem(INSTALLED_KEY, version); localStorage.setItem(BASELINE_KEY, version); } catch (e) {}
      setInstalledVersion(version);
      toast.success(fr ? "Téléchargement de la mise à jour… confirmez l'installation quand vous y êtes invité." : "Downloading update… confirm the install when prompted.", { duration: 6000 });
      setModalOpen(false);
      setDismissed(true);
    } else {
      // PWA: browser download + manual steps.
      setShowSteps(true);
      toast(fr ? "APK téléchargé — suivez les étapes pour l'installer." : "APK downloaded — follow the steps to install.", { icon: "📦", duration: 6000 });
    }
  };

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="android-update-banner"
            className="shrink-0 w-full bg-gradient-to-r from-[var(--rc-gold)] to-[var(--rc-gold-light)] text-black overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-2.5">
              <button onClick={() => setModalOpen(true)} data-testid="android-update-banner-open"
                className="flex-1 flex items-center gap-2.5 text-left min-w-0">
                <ArrowUpCircle size={20} className="shrink-0" />
                <span className="text-sm font-semibold truncate">
                  {fr ? "Nouvelle mise à jour disponible — appuyez pour installer." : "New update available — tap to install."}
                </span>
              </button>
              <button onClick={dismiss} data-testid="android-update-banner-dismiss"
                className="shrink-0 h-8 w-8 grid place-items-center rounded-full hover:bg-black/10 transition-colors" aria-label="Dismiss">
                <X size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="android-update-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.22 }}
              className="relative w-full max-w-sm rounded-3xl border border-[var(--rc-gold)]/25 bg-[var(--rc-bg)] p-6">
              <button onClick={() => setModalOpen(false)} data-testid="android-update-modal-close"
                className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)] transition-colors"><X size={18} /></button>

              <span className="h-14 w-14 grid place-items-center rounded-2xl bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)] mb-4"><Sparkles size={26} /></span>
              <h3 className="font-heading text-2xl mb-1">{fr ? "Nouvelle version Android disponible" : "New Android Version Available"}</h3>
              <p className="text-sm text-[var(--rc-muted)] mb-5" data-testid="android-update-version">
                {fr ? "Version" : "Version"} <span className="text-[var(--rc-gold)] font-semibold">v{version}</span>
                {fmtBytes(latest?.size) ? ` · ${fmtBytes(latest.size)}` : ""}
              </p>

              <button onClick={doUpdate} data-testid="android-update-now-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3.5 text-base hover:bg-[var(--rc-gold-light)] transition-colors">
                <Download size={18} /> {fr ? "Mettre à jour" : "Update Now"}
              </button>

              {ctx.isAndroidPWA && (
                <button onClick={() => setShowSteps((s) => !s)} data-testid="android-how-to-install-btn"
                  className="w-full mt-3 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)] py-3 text-sm hover:text-[var(--rc-gold)] hover:border-[var(--rc-gold)]/40 transition-colors">
                  <Smartphone size={16} /> {fr ? "Comment installer" : "How to Install"}
                </button>
              )}

              {ctx.isNativeAndroid && (
                <p className="mt-4 text-xs text-[var(--rc-muted)] flex items-start gap-2" data-testid="android-native-note">
                  <CheckCircle2 size={14} className="text-[var(--rc-gold)] shrink-0 mt-0.5" />
                  {fr ? "Le téléchargement démarre automatiquement. Appuyez sur « Installer » quand Android vous le demande." : "The download starts automatically. Tap \"Install\" when Android asks you to confirm."}
                </p>
              )}

              {ctx.isAndroidPWA && showSteps && (
                <ol className="mt-4 space-y-2.5" data-testid="android-pwa-steps">
                  {(fr
                    ? ["Appuyez sur l'APK téléchargé dans le navigateur", "Réglages → Autoriser depuis cette source", "Appuyez sur Installer"]
                    : ["Tap the downloaded APK in the browser", "Settings → Allow from this source", "Tap Install"]
                  ).map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--rc-muted)]">
                      <span className="shrink-0 h-6 w-6 grid place-items-center rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] text-xs font-semibold">{i + 1}</span>
                      <span className="flex items-center gap-1.5 pt-0.5">{i === 1 && <Settings2 size={14} className="inline text-[var(--rc-gold)]" />}{s}</span>
                    </li>
                  ))}
                </ol>
              )}

              <button onClick={dismiss} data-testid="android-update-later-btn"
                className="w-full mt-4 text-xs text-[var(--rc-muted)] hover:text-[var(--rc-text)] transition-colors">
                {fr ? "Plus tard" : "Maybe later"}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
